/**
 * What counts as an image the platform will hold.
 *
 * The limits live here rather than in the upload route because three places need the same answer
 * and they must not drift: the Worker rejects on them, the management app filters its file picker
 * by them and refuses before spending a volunteer's data on an upload that will bounce, and the
 * help text quotes them.
 *
 * The list is short on purpose. JPEG, PNG and WebP are what a phone camera and a poster designer
 * actually produce, every browser renders them, and none needs a plugin. HEIC is deliberately
 * absent: an iPhone hands it over happily and roughly nothing else displays it, so accepting it
 * would mean an organiser uploads a poster that looks fine on their phone and is a broken image
 * to half the people reading the page. Safari converts to JPEG on upload, so this costs nothing.
 *
 * SVG is absent for a different reason — it is a document that can carry script, served from our
 * own origin.
 */

import { z } from "zod";

export const IMAGE_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export type ImageMimeType = (typeof IMAGE_MIME_TYPES)[number];

/**
 * 8 MB, measured after the management app has downscaled — which it always does, so nothing a
 * phone produces gets near it. The cap is a backstop against an upload that would sit in the
 * Worker's memory, not a budget an organiser is expected to think about.
 */
export const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

/** The longest edge anything is stored at. A poster on a phone screen is never wider. */
export const MAX_IMAGE_EDGE = 2048;

export const imageMimeSchema = z.enum(IMAGE_MIME_TYPES);

/**
 * Alt text — capped, trimmed, and optional at the schema level.
 *
 * Optional here does not mean unimportant: the management app asks for it every time and says why.
 * But a required field in the API would mean an organiser mid-event, with a delayed Grand Entry to
 * announce, cannot post the photo until they have written a sentence about it — and the version of
 * this that helps nobody is the one where they type "image" to get past it.
 */
export const altTextSchema = z.string().trim().max(300);

export const attachmentMetadataSchema = z.object({
  altText: altTextSchema.optional(),
  width: z.number().int().positive().max(20_000).optional(),
  height: z.number().int().positive().max(20_000).optional(),
});

export const updateAttachmentSchema = z.object({
  altText: altTextSchema.nullable().optional(),
});

export type AttachmentMetadataInput = z.infer<typeof attachmentMetadataSchema>;
export type UpdateAttachmentInput = z.infer<typeof updateAttachmentSchema>;

/** One place that decides whether a file is acceptable, so the API and the browser agree. */
export function imageRejection(
  file: { type: string; size: number },
  formats = IMAGE_MIME_TYPES as readonly string[],
): "format" | "size" | null {
  if (!formats.includes(file.type)) return "format";
  if (file.size > MAX_IMAGE_BYTES) return "size";
  return null;
}
